import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { ModuleName } from '@/constant/modules.constants';
import { useGetPreSignedUrlForUpload } from '@/lib/api/hooks/use-storage';
import {
  buildTextModel,
  loadDocxDocument,
  locate,
  tokenizeDocx,
  type TextModel,
} from '@/lib/docx/tokenize';
import { useGetTemplateVariablesByTemplateId } from '@/modules/templates/hooks/use-template-variable';
import graphqlClient from '@/graphql/graphql-client';
import {
  DELETE_TEMPLATE_VARIABLE_MUTATION,
  INSERT_TEMPLATE_VARIABLE_MUTATION,
  UPDATE_TEMPLATE_DOCX_MUTATION,
  UPDATE_TEMPLATE_VARIABLE_MUTATION,
} from '@/graphql/mutations/templates';
import type { TemplateVariableInsertInput, TemplateVariableUpdateInput } from '@/modules/templates/types/templates.types';

const createTemplateVariable = async (input: TemplateVariableInsertInput) => {
  const response = await graphqlClient.request(INSERT_TEMPLATE_VARIABLE_MUTATION, input);
  return response.insertTemplateVariables?.itemId ?? '';
};

const updateTemplateVariable = (itemId: string, input: TemplateVariableUpdateInput) =>
  graphqlClient.request(UPDATE_TEMPLATE_VARIABLE_MUTATION, { ItemId: itemId, ...input });

const deleteTemplateVariable = (itemId: string) =>
  graphqlClient.request(DELETE_TEMPLATE_VARIABLE_MUTATION, { ItemId: itemId, isHardDelete: true });
import { suggestDocxFields } from '@/modules/templates/services/ai/suggest-docx-fields';
import { anchorSuggestions, documentParagraphs } from './ai-anchor';
import type { TemplateVariable } from '@/modules/templates/types/templates.types';
import { useDocxBuilder } from '../context';
import {
  backfillOccurrences,
  checkPlaceable,
  childName,
  companyChildName,
  companySubKindsTaken,
  NAME_RE,
  subKindsTaken,
  toIdentifier,
  uniqueEntityKey,
  uniqueName,
} from './body-model';
import { arrayMove } from '@dnd-kit/sortable';
import {
  buildDesiredRows,
  buildOrderMap,
  buildScalarSelections,
  contentSignature,
  existingRowSignature,
  hydrateFromRows,
  markingSignature,
  rowSignature,
} from './payload';
import {
  COMPANY_SUB_LABELS,
  COMPANY_SUB_TYPE,
  DEFAULT_CURRENCY,
  SUB_LABELS,
  SUB_TYPE,
  type CompanyMeta,
  type CompanySubKind,
  type DefaultSigner,
  type DraftSelection,
  type MarkedVariable,
  type PersonMeta,
  type Rect,
  type SubKind,
} from './types';

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';

const EMPTY_SIGNER = { name: '', email: '', role: '', company: '' };

function emptyCompanyMeta(key: string, label: string): CompanyMeta {
  return { key, label };
}

function emptyPersonMeta(key: string, label: string): PersonMeta {
  return {
    key,
    label,
    isSignatory: false,
    signingOrder: null,
    companyRef: null,
    companyLabel: null,
    defaultSigner: null,
  };
}

function toDraftSigner(s: DefaultSigner | null | undefined) {
  return { name: s?.name ?? '', email: s?.email ?? '', role: s?.role ?? '', company: s?.company ?? '' };
}

function fromDraftSigner(d: {
  name: string;
  email: string;
  role: string;
  company: string;
}): DefaultSigner | null {
  const out: DefaultSigner = {};
  if (d.name.trim()) out.name = d.name.trim();
  if (d.email.trim()) out.email = d.email.trim();
  if (d.role.trim()) out.role = d.role.trim();
  if (d.company.trim()) out.company = d.company.trim();
  return Object.keys(out).length ? out : null;
}

/**
 * Renumber signatory persons to a contiguous 1..N (no gaps), preserving their current
 * relative order (R3). Non-signers keep signingOrder null. Returns the same map when
 * nothing changes so callers can skip a needless state write.
 */
function normalizeSigningOrders(
  persons: Record<string, PersonMeta>
): Record<string, PersonMeta> {
  const signers = Object.values(persons).filter((p) => p.isSignatory);
  if (signers.length === 0) return persons;
  const sorted = [...signers].sort(
    (a, b) => (a.signingOrder ?? 999) - (b.signingOrder ?? 999) || a.key.localeCompare(b.key)
  );
  let changed = false;
  const next = { ...persons };
  sorted.forEach((p, i) => {
    const order = i + 1;
    if (p.signingOrder !== order) {
      next[p.key] = { ...p, signingOrder: order };
      changed = true;
    }
  });
  return changed ? next : persons;
}

/**
 * Run the tokenizer's own locator against the .docx at MARK time.
 *
 * `checkPlaceable` can only see the rendered DOM. Anything the two models disagree about —
 * text a shape duplicates, glyphs the renderer synthesises — surfaces only here, and if it
 * is not caught now it surfaces at Save, where tokenization is all-or-nothing and one bad
 * span blocks every other field.
 *
 * Returns null (allow) when the bytes are not loaded yet; Save stays the backstop.
 * Signature sub-fields are exempt: they are never tokenized (see buildScalarSelections), so
 * ambiguity there is not fatal — their anchor is resolved later, by paragraph tier.
 */
function checkTokenizable(model: TextModel | null, draft: DraftSelection): string | null {
  if (!model) return null;
  if (draft.kind === 'person' && draft.subKind === 'signature') return null;
  const located = locate(model, {
    name: 'preflight',
    selectedText: draft.selectedText,
    contextBefore: draft.contextBefore,
    contextAfter: draft.contextAfter,
    paragraphIndex: draft.paragraphIndex,
    occurrenceIndex: draft.occurrenceIndex,
    occurrenceCount: draft.occurrenceCount,
  });
  return 'error' in located ? located.error : null;
}

/** A mirror (valueCopyFrom) field, rendered nested under the source it copies from. */
export interface MirrorEntry {
  v: MarkedVariable;
  index: number;
}

/** A leaf (plain field or person sub-field) plus any mirror fields that copy from it. */
export interface LeafEntry {
  v: MarkedVariable;
  index: number;
  mirrors: MirrorEntry[];
}

export type RenderRow =
  | ({ kind: 'field' } & LeafEntry)
  | {
      kind: 'person';
      meta: PersonMeta;
      children: LeafEntry[];
      firstIndex: number;
    }
  | {
      kind: 'company';
      meta: CompanyMeta;
      children: LeafEntry[];
      firstIndex: number;
    };

interface DocxSelectionContextValue {
  // Data
  docxBuffer: ArrayBuffer | null;
  isLoading: boolean;
  /** Document bytes downloaded — required by Save and by AI suggestions, not by the list. */
  isDocumentReady: boolean;
  loadError: string | null;

  // Marking state
  variables: MarkedVariable[];
  persons: Record<string, PersonMeta>;
  companies: Record<string, CompanyMeta>;
  renderRows: RenderRow[];
  draft: DraftSelection | null;
  draftError: string | null;
  fieldErrors: Record<string, string>;
  warnings: string[];
  activeHighlight: string | null;
  isSaving: boolean;
  savedCount: number;

  // AI field suggestions
  /**
   * True only while a suggestion pass could sensibly run: the document is loaded and nothing
   * is marked yet. Gating on an empty panel is what makes this "first time only" without
   * needing a persisted flag — a template that already has fields simply never offers it.
   */
  canSuggest: boolean;
  isSuggesting: boolean;
  suggestionError: string | null;
  suggestFields: () => Promise<void>;

  // Actions
  openDraftFor: (
    selectedText: string,
    contextBefore: string,
    contextAfter: string,
    paragraphIndex: number | null,
    occurrence?: { occurrenceIndex: number | null; occurrenceCount: number | null }
  ) => void;
  startEdit: (index: number) => void;
  updateDraft: (patch: Partial<DraftSelection>) => void;
  commitDraft: () => void;
  cancelDraft: () => void;
  removeVariable: (index: number) => void;
  removePerson: (key: string) => void;
  removeCompany: (key: string) => void;
  reorderRows: (activeId: string, overId: string) => void;
  setActiveHighlight: (name: string | null) => void;
  registerScrollToField: (fn: ((name: string) => void) | null) => void;
  scrollToField: (name: string) => void;
  save: () => Promise<void>;
}

const DocxSelectionContext = createContext<DocxSelectionContextValue | null>(null);

export function useDocxSelection(): DocxSelectionContextValue {
  const ctx = useContext(DocxSelectionContext);
  if (!ctx) throw new Error('useDocxSelection must be used within DocxSelectionProvider');
  return ctx;
}

export function DocxSelectionProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { templateId, docx, step, setSavedCount, setStep } = useDocxBuilder();
  const { mutateAsync: getPreSignedUrl } = useGetPreSignedUrlForUpload();

  // Variables power both step 2 (marking) and step 3 (overview). Deliberately NOT gated on
  // `docx`: the query keys off templateId alone, so waiting for the TemplateDocx row would
  // serialize two independent requests for no reason. The page warms this same key even earlier.
  const varsEnabled = step === 2 || step === 3;
  // The docx bytes are only needed for tokenization on step 2, and do need the row's URL.
  const bufferEnabled = step === 2 && !!docx;

  const [variables, setVariables] = useState<MarkedVariable[]>([]);
  const [persons, setPersons] = useState<Record<string, PersonMeta>>({});
  const [companies, setCompanies] = useState<Record<string, CompanyMeta>>({});
  const [draft, setDraft] = useState<DraftSelection | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [warnings, setWarnings] = useState<string[]>([]);
  const [activeHighlight, setActiveHighlight] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  /**
   * Mirrors `hydratedRef` as STATE, so `canSuggest` can depend on it. The ref alone is not
   * enough: hydration happens in an effect, so there is a painted frame where the fetch has
   * resolved but `variables` is still empty. Offering the AI button in that frame would let a
   * click land suggestions that hydration then overwrites.
   */
  const [hydrated, setHydrated] = useState(false);

  const [docxBuffer, setDocxBuffer] = useState<ArrayBuffer | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const docRootRef = useRef<HTMLElement | null>(null);
  const scrollToFieldRef = useRef<((name: string) => void) | null>(null);
  const hydratedRef = useRef(false);
  // Snapshot of the persisted marking state — lets Save skip re-tokenization when nothing changed.
  const savedSignatureRef = useRef<string | null>(null);
  // Order-insensitive snapshot — lets Save take the order-only fast path when only sequence moved.
  const savedContentSignatureRef = useRef<string | null>(null);

  // Fetch saved variables and hydrate marking state once.
  const { data: varsData, isLoading: isVarsLoading } = useGetTemplateVariablesByTemplateId(
    varsEnabled ? templateId : undefined
  );

  useEffect(() => {
    if (!varsEnabled || hydratedRef.current || isVarsLoading || !varsData) return;
    const {
      variables: v,
      persons: p,
      companies: c,
    } = hydrateFromRows((varsData.items ?? []) as TemplateVariable[]);
    setVariables(v);
    setPersons(p);
    setCompanies(c);
    savedSignatureRef.current = markingSignature(v, p, c);
    savedContentSignatureRef.current = contentSignature(v, p, c);
    hydratedRef.current = true;
    setHydrated(true);
  }, [varsEnabled, isVarsLoading, varsData]);

  // Fetch the ORIGINAL docx bytes (for tokenization + rendering) when step 2 is active.
  useEffect(() => {
    if (!bufferEnabled || !docx?.originalUrl) return;
    let cancelled = false;
    setLoadError(null);
    (async () => {
      try {
        const res = await fetch(docx.originalUrl as string);
        const ab = await res.arrayBuffer();
        if (!cancelled) setDocxBuffer(ab);
      } catch {
        if (!cancelled) setLoadError('Could not load the document.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bufferEnabled, docx?.originalUrl]);

  /**
   * Whether the FIELD LIST can render — variables only.
   *
   * It used to also wait on `docxBuffer`, i.e. on downloading the whole .docx (up to 8 MB)
   * from storage. The list has no use for those bytes: it renders marked fields, which come
   * from a small, fast query. The canvas has its own spinner for the download, so blocking the
   * panel on it only meant staring at two spinners waiting for the same thing.
   */
  const isLoading = isVarsLoading;

  /**
   * Whether the document bytes are in hand. Gates the actions that genuinely need them —
   * Save (tokenization) and the AI suggestion button — so the panel can be interactive before
   * the download finishes without offering something that would silently do nothing.
   */
  const isDocumentReady = !bufferEnabled || (!!docxBuffer && !loadError);

  /**
   * The .docx XML text model, parsed at most once per document and only if something asks
   * for it. Building it costs a zip inflate plus a full XML parse, so it is deliberately not
   * done eagerly when the bytes arrive — most sessions that never commit a mark never pay.
   */
  const xmlModelRef = useRef<{ buffer: ArrayBuffer; model: TextModel | null } | null>(null);
  const getXmlModel = useCallback((): TextModel | null => {
    if (!docxBuffer) return null;
    if (xmlModelRef.current?.buffer !== docxBuffer) {
      let model: TextModel | null = null;
      try {
        model = buildTextModel(loadDocxDocument(docxBuffer).doc);
      } catch {
        model = null; // unparseable — mark-time preflight simply steps aside
      }
      xmlModelRef.current = { buffer: docxBuffer, model };
    }
    return xmlModelRef.current.model;
  }, [docxBuffer]);

  // ── AI field suggestions ──────────────────────────────────────────────────────
  // Offered only while the panel is empty. That single condition does the work a persisted
  // "already analysed" flag would: a template with saved fields hydrates them and never shows
  // the button, so this is "first time only" derived from state rather than tracked. It also
  // makes clobbering impossible — there is never anything to clobber when it can run.
  //
  // `!!docx` is required on its own: `docx` is null while ['templateDocxs'] resolves, and in
  // that window varsEnabled/bufferEnabled are false so `isLoading` is ALSO false — without it
  // the button would flash on a template that already has fields.
  const canSuggest =
    !!docx && hydrated && !isLoading && !!docxBuffer && variables.length === 0;

  const suggestFields = useCallback(async () => {
    const model = getXmlModel();
    if (!model) {
      setSuggestionError('The document could not be read. Try reloading the page.');
      return;
    }

    setIsSuggesting(true);
    setSuggestionError(null);

    // The transport has no wall-clock timeout of its own, so the caller must impose one or a
    // stalled request would leave the panel spinning forever.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort('Suggestion timeout exceeded'), 120_000);

    try {
      const paragraphs = documentParagraphs(model);
      const { suggestions, omittedParagraphs } = await suggestDocxFields({
        paragraphs,
        documentName: docx?.originalName,
        signal: controller.signal,
      });

      // Every span is derived here, against the tokenizer's own model — nothing positional
      // from the agent is trusted, and anything that cannot be anchored is dropped.
      const anchored = anchorSuggestions(model, suggestions, { variables, persons, companies });

      if (anchored.variables.length === variables.length) {
        setSuggestionError(
          "The AI didn't find any fields it could place in this document. Mark them by hand instead."
        );
        return;
      }

      setVariables(anchored.variables);
      setPersons(anchored.persons);
      setCompanies(anchored.companies);

      const added = anchored.variables.length - variables.length;
      // Never imply full coverage: say what was skipped so the user knows to scan for gaps.
      const caveats = [
        anchored.dropped > 0 ? `${anchored.dropped} couldn't be matched` : '',
        omittedParagraphs > 0 ? `${omittedParagraphs} paragraph(s) not analysed` : '',
      ].filter(Boolean);
      toast({
        variant: 'success',
        title:
          `Suggested ${added} field${added === 1 ? '' : 's'}` +
          (caveats.length ? ` — ${caveats.join(', ')}.` : '. Review and remove any you don’t need.'),
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setSuggestionError('The request took too long. Please try again.');
        return;
      }
      const message = err instanceof Error ? err.message : 'Could not suggest fields.';
      setSuggestionError(message);
      toast({ variant: 'destructive', title: message });
    } finally {
      clearTimeout(timeout);
      setIsSuggesting(false);
    }
  }, [getXmlModel, docx?.originalName, variables, persons, companies, toast]);

  // ── Derived render rows (fields + person groups in first-appearance order) ──────
  // Mirror (valueCopyFrom) fields are lifted out of the top level and nested under the
  // leaf they copy from (a plain field OR a person sub-field), collapsed by default.
  const renderRows = ((): RenderRow[] => {
    const nameSet = new Set(variables.map((v) => v.name));
    const isMirror = (v: MarkedVariable) =>
      !!v.valueCopyFrom && !v.personKey && !v.companyKey && nameSet.has(v.valueCopyFrom);

    const mirrorsBySource = new Map<string, MirrorEntry[]>();
    variables.forEach((v, index) => {
      if (isMirror(v)) {
        const key = v.valueCopyFrom as string;
        const arr = mirrorsBySource.get(key) ?? [];
        arr.push({ v, index });
        mirrorsBySource.set(key, arr);
      }
    });

    const rows: RenderRow[] = [];
    const byPersonKey = new Map<string, Extract<RenderRow, { kind: 'person' }>>();
    const byCompanyKey = new Map<string, Extract<RenderRow, { kind: 'company' }>>();
    variables.forEach((v, index) => {
      if (isMirror(v)) return; // rendered nested under its source leaf
      const leaf: LeafEntry = { v, index, mirrors: mirrorsBySource.get(v.name) ?? [] };
      if (v.personKey) {
        const key = v.personKey;
        let row = byPersonKey.get(key);
        if (!row) {
          const meta = persons[key] ?? emptyPersonMeta(key, key);
          row = { kind: 'person', meta, children: [], firstIndex: index };
          byPersonKey.set(key, row);
          rows.push(row);
        }
        row.children.push(leaf);
      } else if (v.companyKey) {
        const key = v.companyKey;
        let row = byCompanyKey.get(key);
        if (!row) {
          const meta = companies[key] ?? emptyCompanyMeta(key, key);
          row = { kind: 'company', meta, children: [], firstIndex: index };
          byCompanyKey.set(key, row);
          rows.push(row);
        }
        row.children.push(leaf);
      } else {
        rows.push({ kind: 'field', ...leaf });
      }
    });
    return rows;
  })();

  const savedCount = renderRows.length;

  // Person and company keys share one namespace, so key allocation must see both.
  const entityKeys = useMemo(
    () => [...Object.keys(persons), ...Object.keys(companies)],
    [persons, companies]
  );

  // ── Draft management ────────────────────────────────────────────────────────
  const openDraftFor = useCallback(
    (
      selectedText: string,
      contextBefore: string,
      contextAfter: string,
      paragraphIndex: number | null,
      occurrence?: { occurrenceIndex: number | null; occurrenceCount: number | null }
    ) => {
      const label = selectedText.length > 60 ? `${selectedText.slice(0, 57)}…` : selectedText;
      setDraftError(null);
      setDraft({
        selectedText,
        contextBefore,
        contextAfter,
        paragraphIndex,
        occurrenceIndex: occurrence?.occurrenceIndex ?? null,
        occurrenceCount: occurrence?.occurrenceCount ?? null,
        name: uniqueName(variables, toIdentifier(label), undefined, [
          ...Object.keys(persons),
          ...Object.keys(companies),
        ]),
        label,
        type: 'text',
        required: true,
        options: [],
        placeholder: '',
        helpText: '',
        currencyCode: DEFAULT_CURRENCY,
        valueCopyFrom: '',
        editingIndex: null,
        kind: 'field',
        personKey: uniqueEntityKey(variables, entityKeys, toIdentifier(label)),
        personLabel: label,
        subKind: 'name',
        newPerson: true,
        companyKey: uniqueEntityKey(variables, entityKeys, toIdentifier(label)),
        companyEntityLabel: label,
        companySubKind: 'name',
        newCompany: true,
        isSignatory: false,
        signingOrder: null,
        companyRef: null,
        companyLabel: null,
        defaultSigner: { ...EMPTY_SIGNER },
      });
      window.getSelection()?.removeAllRanges();
    },
    [variables, persons, companies, entityKeys]
  );

  const startEdit = useCallback(
    (index: number) => {
      const v = variables[index];
      if (!v) return;
      setActiveHighlight(v.name);
      setDraftError(null);
      const meta = v.personKey ? persons[v.personKey] : undefined;
      const coMeta = v.companyKey ? companies[v.companyKey] : undefined;
      setDraft({
        selectedText: v.selectedText,
        contextBefore: v.contextBefore,
        contextAfter: v.contextAfter,
        paragraphIndex: v.paragraphIndex,
        // Editing a field's settings never moves its span, so the marked occurrence carries over.
        occurrenceIndex: v.occurrenceIndex ?? null,
        occurrenceCount: v.occurrenceCount ?? null,
        name: v.name,
        label: v.label,
        type: v.type,
        required: v.required,
        options: v.options,
        placeholder: v.placeholder ?? '',
        helpText: v.helpText ?? '',
        currencyCode: v.currencyCode ?? DEFAULT_CURRENCY,
        valueCopyFrom: v.valueCopyFrom ?? '',
        editingIndex: index,
        kind: v.personKey ? 'person' : v.companyKey ? 'company' : 'field',
        personKey: v.personKey ?? uniqueEntityKey(variables, entityKeys, toIdentifier(v.label)),
        personLabel: meta?.label ?? v.label,
        subKind: v.subKind ?? 'name',
        newPerson: false,
        companyKey: v.companyKey ?? uniqueEntityKey(variables, entityKeys, toIdentifier(v.label)),
        companyEntityLabel: coMeta?.label ?? v.label,
        companySubKind: v.companySubKind ?? 'name',
        newCompany: false,
        isSignatory: meta?.isSignatory ?? false,
        signingOrder: meta?.signingOrder ?? null,
        companyRef: meta?.companyRef ?? null,
        companyLabel: meta?.companyLabel ?? null,
        defaultSigner: toDraftSigner(meta?.defaultSigner),
      });
    },
    [variables, persons, companies, entityKeys]
  );

  const updateDraft = useCallback((patch: Partial<DraftSelection>) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
  }, []);

  const cancelDraft = useCallback(() => {
    setDraft(null);
    setDraftError(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const commitDraft = useCallback(() => {
    if (!draft) return;
    const editing = draft.editingIndex;
    // Two checks, in the two models that matter. checkPlaceable inspects the RENDER (it is
    // the only one that can see overlaps with other marked spans); checkTokenizable runs the
    // exact call save will make against the XML. Without the second, a span the render
    // resolves but the .docx does not stays invisible until Save — where it is fatal for the
    // whole batch and the selection is long gone from the screen.
    const placeError =
      checkPlaceable(
        docRootRef.current,
        variables,
        draft.selectedText,
        draft.contextBefore,
        draft.contextAfter,
        editing,
        { occurrenceIndex: draft.occurrenceIndex, occurrenceCount: draft.occurrenceCount }
      ) ?? checkTokenizable(getXmlModel(), draft);

    // ── Person sub-field ────────────────────────────────────────────────────────
    if (draft.kind === 'person') {
      const key = draft.newPerson ? draft.personKey.trim() : draft.personKey;
      const sub = draft.subKind;
      if (!NAME_RE.test(key) || key.includes('__')) {
        setDraftError(
          'Person reference must start with a letter/underscore, use only letters, numbers and underscores, and no “__”.'
        );
        return;
      }
      if (draft.newPerson) {
        const collide =
          variables.some((v) => v.name === key) ||
          !!persons[key] ||
          variables.some((v) => v.name.startsWith(`${key}__`) && v.personKey !== key);
        if (collide) {
          setDraftError('A field or person with this reference already exists.');
          return;
        }
      }
      const cName = childName(key, sub);
      const editingName = editing !== null ? variables[editing]?.name : undefined;
      if (subKindsTaken(variables, key, editingName).has(sub)) {
        setDraftError(`This person already has a ${SUB_LABELS[sub].toLowerCase()} field.`);
        return;
      }
      if (placeError) {
        setDraftError(placeError);
        return;
      }
      const personLabel = draft.personLabel.trim() || key;
      const child: MarkedVariable = {
        name: cName,
        label: `${personLabel} — ${SUB_LABELS[sub]}`,
        type: SUB_TYPE[sub],
        required: draft.required,
        options: [],
        selectedText: draft.selectedText,
        contextBefore: draft.contextBefore,
        contextAfter: draft.contextAfter,
        paragraphIndex: draft.paragraphIndex,
        occurrenceIndex: draft.occurrenceIndex,
        occurrenceCount: draft.occurrenceCount,
        personKey: key,
        subKind: sub,
      };
      setVariables((prev) => {
        let next =
          editing !== null ? prev.map((v, i) => (i === editing ? child : v)) : [...prev, child];
        // If the person's label changed (via the Name slot), refresh sibling child labels.
        if (sub === 'name') {
          next = next.map((v) =>
            v.personKey === key && v.subKind && v.name !== cName
              ? { ...v, label: `${personLabel} — ${SUB_LABELS[v.subKind]}` }
              : v
          );
        }
        return next;
      });
      setPersons((prev) => {
        const existing = prev[key] ?? emptyPersonMeta(key, personLabel);
        const meta: PersonMeta = { ...existing };
        // Name slot owns identity + signatory + default signer.
        if (sub === 'name') {
          meta.label = personLabel;
          meta.isSignatory = draft.isSignatory;
          meta.signingOrder = draft.isSignatory ? draft.signingOrder ?? 1 : null;
          meta.defaultSigner = fromDraftSigner(draft.defaultSigner);
        }
        // Company slot owns the shared-company link.
        if (sub === 'company') {
          meta.companyRef = draft.companyRef;
          meta.companyLabel = draft.companyLabel;
        }
        return { ...prev, [key]: meta };
      });
      setFieldErrors((prev) => {
        const copy = { ...prev };
        if (editingName) delete copy[editingName];
        delete copy[cName];
        return copy;
      });
      setActiveHighlight(cName);
      setDraft(null);
      setDraftError(null);
      window.getSelection()?.removeAllRanges();
      return;
    }

    // ── Company sub-field ─────────────────────────────────────────────────────
    if (draft.kind === 'company') {
      const key = draft.newCompany ? draft.companyKey.trim() : draft.companyKey;
      const sub = draft.companySubKind;
      if (!NAME_RE.test(key) || key.includes('__')) {
        setDraftError(
          'Company reference must start with a letter/underscore, use only letters, numbers and underscores, and no “__”.'
        );
        return;
      }
      if (draft.newCompany) {
        const collide =
          variables.some((v) => v.name === key) ||
          !!companies[key] ||
          !!persons[key] ||
          variables.some(
            (v) => v.name.startsWith(`${key}__`) && v.companyKey !== key && v.personKey !== key
          );
        if (collide) {
          setDraftError('A field, person or company with this reference already exists.');
          return;
        }
      }
      const cName = companyChildName(key, sub);
      const editingName = editing !== null ? variables[editing]?.name : undefined;
      if (companySubKindsTaken(variables, key, editingName).has(sub)) {
        setDraftError(`This company already has a ${COMPANY_SUB_LABELS[sub].toLowerCase()} field.`);
        return;
      }
      if (placeError) {
        setDraftError(placeError);
        return;
      }
      const companyLabelText = draft.companyEntityLabel.trim() || key;
      const child: MarkedVariable = {
        name: cName,
        label: `${companyLabelText} — ${COMPANY_SUB_LABELS[sub]}`,
        type: COMPANY_SUB_TYPE[sub],
        required: draft.required,
        options: [],
        selectedText: draft.selectedText,
        contextBefore: draft.contextBefore,
        contextAfter: draft.contextAfter,
        paragraphIndex: draft.paragraphIndex,
        occurrenceIndex: draft.occurrenceIndex,
        occurrenceCount: draft.occurrenceCount,
        companyKey: key,
        companySubKind: sub,
      };
      setVariables((prev) => {
        let next =
          editing !== null ? prev.map((v, i) => (i === editing ? child : v)) : [...prev, child];
        // If the company's label changed (via the Name slot), refresh sibling child labels.
        if (sub === 'name') {
          next = next.map((v) =>
            v.companyKey === key && v.companySubKind && v.name !== cName
              ? { ...v, label: `${companyLabelText} — ${COMPANY_SUB_LABELS[v.companySubKind]}` }
              : v
          );
        }
        return next;
      });
      setCompanies((prev) => {
        const existing = prev[key] ?? emptyCompanyMeta(key, companyLabelText);
        // The Name slot owns the company's identity, exactly as it does for a person.
        const meta: CompanyMeta =
          sub === 'name' ? { ...existing, label: companyLabelText } : { ...existing };
        return { ...prev, [key]: meta };
      });
      setFieldErrors((prev) => {
        const copy = { ...prev };
        if (editingName) delete copy[editingName];
        delete copy[cName];
        return copy;
      });
      setActiveHighlight(cName);
      setDraft(null);
      setDraftError(null);
      window.getSelection()?.removeAllRanges();
      return;
    }

    // ── Plain field ───────────────────────────────────────────────────────────
    const name = draft.name.trim();
    if (!NAME_RE.test(name) || name.includes('__')) {
      setDraftError(
        'Name must start with a letter or underscore, use only letters, numbers and underscores, and no “__”.'
      );
      return;
    }
    const clashes =
      variables.some((v, i) => v.name === name && i !== editing) ||
      !!persons[name] ||
      !!companies[name];
    if (clashes) {
      setDraftError('A field, person or company with this name already exists.');
      return;
    }
    const options = draft.type === 'select' ? draft.options.filter(Boolean) : [];
    if (draft.type === 'select' && options.length === 0) {
      setDraftError('Add at least one choice.');
      return;
    }
    if (placeError) {
      setDraftError(placeError);
      return;
    }
    // A "copy" mirrors a source field — a plain field OR a person sub-field (e.g. a
    // person's Name/Company). Guard against self / chain / stale links; a source may not
    // itself be a copy (no chaining).
    const source = draft.valueCopyFrom
      ? variables.find((v) => v.name === draft.valueCopyFrom && !v.valueCopyFrom)
      : undefined;
    const valueCopyFrom = source && source.name !== name ? source.name : undefined;

    const next: MarkedVariable = {
      name,
      label: draft.label.trim() || name,
      type: draft.type,
      required: draft.required,
      options,
      placeholder: draft.placeholder.trim() || undefined,
      helpText: draft.helpText.trim() || undefined,
      currencyCode: draft.type === 'currency' ? draft.currencyCode : undefined,
      valueCopyFrom,
      selectedText: draft.selectedText,
      contextBefore: draft.contextBefore,
      contextAfter: draft.contextAfter,
      paragraphIndex: draft.paragraphIndex,
      occurrenceIndex: draft.occurrenceIndex,
      occurrenceCount: draft.occurrenceCount,
    };
    setVariables((prev) =>
      editing !== null ? prev.map((v, i) => (i === editing ? next : v)) : [...prev, next]
    );
    setFieldErrors((prev) => {
      const copy = { ...prev };
      if (editing !== null && variables[editing]) delete copy[variables[editing].name];
      delete copy[name];
      return copy;
    });
    setActiveHighlight(name);
    setDraft(null);
    setDraftError(null);
    window.getSelection()?.removeAllRanges();
  }, [draft, variables, persons, companies, getXmlModel]);

  const removePerson = useCallback((key: string) => {
    setVariables((prev) => prev.filter((v) => v.personKey !== key));
    setPersons((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    setActiveHighlight((cur) => (cur && cur.startsWith(`${key}__`) ? null : cur));
  }, []);

  const removeCompany = useCallback((key: string) => {
    setVariables((prev) => prev.filter((v) => v.companyKey !== key));
    setCompanies((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    setActiveHighlight((cur) => (cur && cur.startsWith(`${key}__`) ? null : cur));
  }, []);

  const removeVariable = useCallback(
    (index: number) => {
      const removed = variables[index];
      if (!removed) return;
      // Removing a person's Name removes the whole person (a person needs a name).
      if (removed.personKey && removed.subKind === 'name') {
        removePerson(removed.personKey);
        return;
      }
      // Same for a company — its name is its identity.
      if (removed.companyKey && removed.companySubKind === 'name') {
        removeCompany(removed.companyKey);
        return;
      }
      setVariables((prev) =>
        prev
          .filter((_, i) => i !== index)
          // Any field that mirrored the removed one becomes independent (no dangling ref).
          .map((v) => (v.valueCopyFrom === removed.name ? { ...v, valueCopyFrom: undefined } : v))
      );
      setFieldErrors((prev) => {
        const copy = { ...prev };
        delete copy[removed.name];
        return copy;
      });
      setActiveHighlight((cur) => (cur === removed.name ? null : cur));
    },
    [variables, removePerson, removeCompany]
  );

  // ── Reorder top-level rows (drag & drop) ──────────────────────────────────────
  // activeId/overId are the row ids used by VariableList: `field:${name}` / `person:${key}`.
  // A person group moves as one contiguous block; the flattened result becomes `variables`,
  // so array order is display order and person children stay contiguous.
  const reorderRows = useCallback((activeId: string, overId: string) => {
    if (activeId === overId) return;
    setVariables((prev) => {
      const nameSet = new Set(prev.map((v) => v.name));
      const isMirror = (v: MarkedVariable) =>
        !!v.valueCopyFrom && !v.personKey && !v.companyKey && nameSet.has(v.valueCopyFrom);
      // A leaf's mirror fields follow it so they stay contiguous after flattening.
      const mirrorsBySource = new Map<string, MarkedVariable[]>();
      prev.forEach((v) => {
        if (isMirror(v)) {
          const arr = mirrorsBySource.get(v.valueCopyFrom as string) ?? [];
          arr.push(v);
          mirrorsBySource.set(v.valueCopyFrom as string, arr);
        }
      });
      const withMirrors = (v: MarkedVariable) => [v, ...(mirrorsBySource.get(v.name) ?? [])];

      // Rebuild top-level rows (field | person group) in first-appearance order.
      const rows: Array<{ id: string; vars: MarkedVariable[] }> = [];
      const byKey = new Map<string, { id: string; vars: MarkedVariable[] }>();
      prev.forEach((v) => {
        if (isMirror(v)) return; // moves with its source
        // A person/company group is keyed by its entity key so all its children stay
        // contiguous; entity keys share one namespace, so one map is enough.
        const groupId = v.personKey
          ? `person:${v.personKey}`
          : v.companyKey
            ? `company:${v.companyKey}`
            : null;
        if (groupId) {
          let row = byKey.get(groupId);
          if (!row) {
            row = { id: groupId, vars: [] };
            byKey.set(groupId, row);
            rows.push(row);
          }
          row.vars.push(...withMirrors(v));
        } else {
          rows.push({ id: `field:${v.name}`, vars: withMirrors(v) });
        }
      });
      const from = rows.findIndex((r) => r.id === activeId);
      const to = rows.findIndex((r) => r.id === overId);
      if (from === -1 || to === -1) return prev;
      return arrayMove(rows, from, to).flatMap((r) => r.vars);
    });
  }, []);

  // ── Scroll-to-field (canvas registers the impl) ───────────────────────────────
  const registerScrollToField = useCallback((fn: ((name: string) => void) | null) => {
    scrollToFieldRef.current = fn;
  }, []);

  const scrollToField = useCallback((name: string) => {
    setActiveHighlight(name);
    scrollToFieldRef.current?.(name);
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!docx?.id || !docxBuffer) return;
    if (variables.length === 0) {
      toast({ variant: 'destructive', title: 'Mark at least one field before saving.' });
      return;
    }
    // A saved person must carry a Name sub-field.
    const personKeysInUse = new Set(
      variables.filter((v) => v.personKey).map((v) => v.personKey as string)
    );
    for (const key of personKeysInUse) {
      const hasName = variables.some((v) => v.personKey === key && v.subKind === 'name');
      if (!hasName) {
        toast({
          variant: 'destructive',
          title: `“${persons[key]?.label ?? key}” needs a Name before saving.`,
        });
        return;
      }
    }
    // A saved company must carry a Company name sub-field — its identity slot.
    const companyKeysInUse = new Set(
      variables.filter((v) => v.companyKey).map((v) => v.companyKey as string)
    );
    for (const key of companyKeysInUse) {
      const hasName = variables.some(
        (v) => v.companyKey === key && v.companySubKind === 'name'
      );
      if (!hasName) {
        toast({
          variant: 'destructive',
          title: `“${companies[key]?.label ?? key}” needs a Company name before saving.`,
        });
        return;
      }
    }

    // Renumber signers to a contiguous 1..N before persisting (R3, no gaps).
    const normalizedPersons = normalizeSigningOrders(persons);
    if (normalizedPersons !== persons) setPersons(normalizedPersons);

    // Nothing changed since load → skip re-tokenization entirely, just continue.
    const currentSig = markingSignature(variables, normalizedPersons, companies);
    if (savedSignatureRef.current !== null && currentSig === savedSignatureRef.current) {
      setStep(3);
      return;
    }

    // Only the row ORDER moved (content unchanged) → skip tokenize/upload/version bump,
    // just rewrite the Order field on moved rows.
    const currentContentSig = contentSignature(variables, normalizedPersons, companies);
    if (
      savedContentSignatureRef.current !== null &&
      currentContentSig === savedContentSignatureRef.current
    ) {
      setIsSaving(true);
      try {
        const orderMap = buildOrderMap(variables);
        const existing = (varsData?.items ?? []) as TemplateVariable[];
        for (const r of existing) {
          const desiredOrder = orderMap.get(r.name ?? '');
          if (desiredOrder !== undefined && r.order !== desiredOrder) {
            await updateTemplateVariable(r.id, { Order: desiredOrder });
          }
        }
        await queryClient.invalidateQueries({ queryKey: ['templateVariables', templateId] });
        savedSignatureRef.current = currentSig;
        savedContentSignatureRef.current = currentContentSig;
        setStep(3);
      } catch (err) {
        toast({
          variant: 'destructive',
          title: err instanceof Error ? err.message : 'Failed to update field order.',
        });
      } finally {
        setIsSaving(false);
      }
      return;
    }

    setIsSaving(true);
    setFieldErrors({});
    setWarnings([]);
    try {
      // 1. Tokenize the ORIGINAL doc from scratch (all-or-nothing). Fields marked before
      //    occurrence tracking existed get an ordinal first, where the render is certain.
      const markedVariables = backfillOccurrences(docRootRef.current, variables);
      const selections = buildScalarSelections(markedVariables);
      const result = tokenizeDocx(docxBuffer, selections);
      if (result.failures.length > 0 || !result.buffer) {
        const map: Record<string, string> = {};
        for (const f of result.failures) map[f.name] = f.reason;
        setFieldErrors(map);
        const first = variables.find((v) => map[v.name])?.name;
        if (first) scrollToField(first);
        toast({
          variant: 'destructive',
          title: `Nothing saved — ${result.failures.length} field(s) couldn't be placed. Fix or remove them, then save again.`,
        });
        return;
      }
      setWarnings(result.warnings);

      // 2. Upload tokenized .docx to Blocks storage.
      const fileName =
        (docx.originalName || 'template').replace(/\.docx$/i, '') + '.tokenized.docx';
      const presigned = await getPreSignedUrl({
        name: fileName,
        projectKey,
        itemId: '',
        metaData: '',
        accessModifier: 'Public',
        configurationName: 'Default',
        parentDirectoryId: '',
        tags: '',
        moduleName: ModuleName.DefaultConstruct,
      });
      if (!presigned.isSuccess || !presigned.uploadUrl) {
        throw new Error('Failed to get upload URL from storage.');
      }
      await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: new Blob([result.buffer as BlobPart], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        }),
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'x-ms-blob-type': 'BlockBlob',
        },
      });
      const tokenizedUrl = presigned.uploadUrl.split('?')[0];
      const tokenizedKey = presigned.fileId || '';

      // 3. Update the TemplateDocx row: tokenized location, bump version, mark ready.
      await graphqlClient.request(UPDATE_TEMPLATE_DOCX_MUTATION, {
        ItemId: docx.id,
        TokenizedUrl: tokenizedUrl,
        TokenizedKey: tokenizedKey,
        TokenVersion: (docx.tokenVersion ?? 0) + 1,
        Status: 'ready',
      });

      // 4. Diff/merge the TemplateVariable rows (match by Name; preserve ItemIds).
      const existing = (varsData?.items ?? []) as TemplateVariable[];
      const byName = new Map(existing.map((r) => [r.name ?? '', r]));
      const desired = buildDesiredRows(
        templateId,
        markedVariables,
        normalizedPersons,
        companies,
        result.resolved
      );

      const desiredNames = new Set<string>([
        ...desired.persons.map((p) => p.key),
        ...desired.companies.map((c) => c.key),
        ...desired.fields.map((f) => f.name),
        ...desired.children.map((c) => c.name),
      ]);

      // Parents first — capture each key's ItemId for children. Person and company keys
      // share one namespace, so one map serves both kinds.
      const parentIdByKey = new Map<string, string>();
      for (const p of [...desired.persons, ...desired.companies]) {
        const prev = byName.get(p.key);
        if (prev) {
          parentIdByKey.set(p.key, prev.id);
          if (existingRowSignature(prev) !== rowSignature(p.input)) {
            await updateTemplateVariable(prev.id, p.input);
          }
        } else {
          const id = await createTemplateVariable(p.input);
          parentIdByKey.set(p.key, id);
        }
      }

      // Plain fields.
      for (const f of desired.fields) {
        const prev = byName.get(f.name);
        if (prev) {
          if (existingRowSignature(prev) !== rowSignature(f.input)) {
            await updateTemplateVariable(prev.id, f.input);
          }
        } else {
          await createTemplateVariable(f.input);
        }
      }

      // Person + company children (ParentId resolved from parentIdByKey).
      for (const c of desired.children) {
        const parentId = parentIdByKey.get(c.parentKey) ?? null;
        const input = { ...c.input, ParentId: parentId };
        const prev = byName.get(c.name);
        if (prev) {
          const changed =
            existingRowSignature(prev) !== rowSignature(c.input) || prev.parentId !== parentId;
          if (changed) await updateTemplateVariable(prev.id, input);
        } else {
          await createTemplateVariable(input);
        }
      }

      // Deletes — existing rows no longer desired.
      for (const r of existing) {
        if (!desiredNames.has(r.name ?? '')) {
          await deleteTemplateVariable(r.id);
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['templateVariables', templateId] });
      await queryClient.invalidateQueries({ queryKey: ['templateDocxs', templateId] });
      setSavedCount(desired.persons.length + desired.companies.length + desired.fields.length);
      // Record the new persisted snapshot so an immediate re-save is a no-op.
      savedSignatureRef.current = currentSig;
      savedContentSignatureRef.current = currentContentSig;
      toast({
        variant: 'success',
        title: `Saved ${desiredNames.size} field${desiredNames.size === 1 ? '' : 's'} — your template is ready.`,
      });
      setStep(3);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: err instanceof Error ? err.message : 'Failed to save fields.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    docx,
    docxBuffer,
    variables,
    persons,
    companies,
    varsData,
    templateId,
    getPreSignedUrl,
    queryClient,
    setSavedCount,
    setStep,
    scrollToField,
    toast,
  ]);

  return (
    <DocxSelectionContext.Provider
      value={{
        docxBuffer,
        isLoading,
        isDocumentReady,
        loadError,
        variables,
        persons,
        companies,
        renderRows,
        draft,
        draftError,
        fieldErrors,
        warnings,
        activeHighlight,
        isSaving,
        savedCount,
        canSuggest,
        isSuggesting,
        suggestionError,
        suggestFields,
        openDraftFor,
        startEdit,
        updateDraft,
        commitDraft,
        cancelDraft,
        removeVariable,
        removePerson,
        removeCompany,
        reorderRows,
        setActiveHighlight,
        registerScrollToField,
        scrollToField,
        save,
      }}
    >
      <DocRootBridge docRootRef={docRootRef}>{children}</DocRootBridge>
    </DocxSelectionContext.Provider>
  );
}

/**
 * Shares the rendered-doc root ref with the provider (needed by checkPlaceable at
 * commit time) without threading a second context value. The canvas uses this ref
 * object directly as the ref for its document container.
 */
const DocRootContext = createContext<MutableRefObject<HTMLElement | null> | null>(null);

function DocRootBridge({
  docRootRef,
  children,
}: {
  docRootRef: MutableRefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  return <DocRootContext.Provider value={docRootRef}>{children}</DocRootContext.Provider>;
}

/** The shared doc-root ref — assign it to the document container element in the canvas. */
export function useDocRootRef(): MutableRefObject<HTMLElement | null> {
  const ref = useContext(DocRootContext);
  if (!ref) throw new Error('useDocRootRef must be used within DocxSelectionProvider');
  return ref;
}

// Re-export types the canvas/panel need alongside the hook.
export type { CompanyMeta, CompanySubKind, MarkedVariable, PersonMeta, Rect, SubKind };
